import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Avatar, Button, DatePicker, Select, Spin, App } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  apiCheckAvailableSlots,
  apiGetAppointmentDetails,
} from "../../api/appointments";
import http from "../../services/http";

const AppointmentBooking = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [patientData, setPatientData] = useState(
    location.state?.patient || null
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timePeriod, setTimePeriod] = useState("Morning");
  const [allSlots, setAllSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const isRescheduleMode = !!id;

  useEffect(() => {
    const init = async () => {
      if (isRescheduleMode) {
        await fetchAppointmentDetails();
      } else {
        setLoading(false);
      }
      setInitialLoadDone(true);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRescheduleMode]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGetAppointmentDetails(id);
      const appointmentData = response.data || response;

      if (!appointmentData) {
        throw new Error("Invalid response structure");
      }

      const newPatientData = {
        id: appointmentData.patientId,
        patientId: appointmentData.patientId,
        name: appointmentData.fullName,
        fullName: appointmentData.fullName,
        profileImage: null,
      };

      setAppointment(appointmentData);
      setPatientData(newPatientData);

      if (appointmentData.appointmentTimestamp) {
        const appointmentDateTime = dayjs.unix(
          appointmentData.appointmentTimestamp
        );
        const hour = appointmentDateTime.hour();

        let period = "Morning";
        if (hour >= 12 && hour < 17) {
          period = "Afternoon";
        }

        setTimePeriod(period);
        setSelectedDate(appointmentDateTime);
        setSelectedTime({
          timestamp: appointmentData.appointmentTimestamp,
          isAvailable: true,
        });

        await fetchAvailableSlots(
          newPatientData.patientId,
          appointmentDateTime,
          period
        );
      }
    } catch (error) {
      console.error("Error fetching appointment:", error);
      message.error("Failed to load appointment details");
      setAppointment(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (patId, date, period) => {
    const patientId = patId || patientData?.patientId || patientData?.id;
    const useDate = date || selectedDate;
    const usePeriod = period || timePeriod;

    if (!patientId || !useDate) {
      return;
    }

    try {
      setLoadingSlots(true);

      const timePeriodMap = {
        Morning: 1,
        Afternoon: 2,
      };

      const timestamp = useDate.startOf("day").unix();

      const payload = {
        patientId: parseInt(patientId),
        timestamp: timestamp,
        timePeriod: timePeriodMap[usePeriod],
      };

      const response = await apiCheckAvailableSlots(payload);
      const slotsData =
        response.data?.availableSlots || response.availableSlots || [];

      setAllSlots(slotsData);

      if (isRescheduleMode && appointment?.appointmentTimestamp) {
        const existingSlot = slotsData.find(
          (slot) => slot.timestamp === appointment.appointmentTimestamp
        );
        if (existingSlot) {
          setSelectedTime(existingSlot);
        }
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
      message.error("Failed to load available slots");
      setAllSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (initialLoadDone && selectedDate && patientData?.patientId) {
      fetchAvailableSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, timePeriod]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (slot) => {
    if (slot.isAvailable) {
      setSelectedTime(slot);
    }
  };

  const formatSlotTime = (timestamp) => {
    return dayjs.unix(timestamp).format("hh:mm A");
  };

  const handleCreateAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      message.warning("Please select both date and time");
      return;
    }

    const patId = patientData?.patientId || patientData?.id;
    if (!patId) {
      message.error("Patient information is missing");
      return;
    }

    try {
      setSubmitting(true);

      const appointmentData = {
        appointmentTimestamp: selectedTime.timestamp,
        appointmentType: 3,
        patientNotes: "",
        patientId: parseInt(patId),
      };

      await http.post("/api/patient/appointment/book", appointmentData);

      message.success("Appointment created successfully");
      navigate("/appointments");
    } catch (error) {
      console.error("Error creating appointment:", error);
      message.error(
        error.response?.data?.message || "Failed to create appointment"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      message.warning("Please select both date and time");
      return;
    }

    try {
      setSubmitting(true);

      const rescheduleData = {
        appointmentId: parseInt(id),
        appointmentTimestamp: selectedTime.timestamp,
      };

      await http.post("/api/patient/appointment/reschedule", rescheduleData);

      message.success("Appointment rescheduled successfully");
      navigate("/appointments");
    } catch (error) {
      console.error("Error rescheduling appointment:", error);
      message.error(
        error.response?.data?.message || "Failed to reschedule appointment"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (isRescheduleMode) {
      handleReschedule();
    } else {
      handleCreateAppointment();
    }
  };

  const disabledDate = (current) => {
    return current && current < dayjs().startOf("day");
  };

  const availableSlotsCount = allSlots.filter(
    (slot) => slot.isAvailable
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isRescheduleMode && !appointment) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Appointment Not Found
            </h2>
            <Button type="primary" onClick={() => navigate("/appointments")}>
              Back to Appointments
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!isRescheduleMode && !patientData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Patient Information Missing
            </h2>
            <p className="text-gray-500 mb-6">
              Unable to create appointment without patient information.
            </p>
            <Button type="primary" onClick={() => navigate("/follow-ups")}>
              Back to Follow-ups
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() =>
          navigate(isRescheduleMode ? "/appointments" : "/follow-ups")
        }
        className="mb-4"
        type="text"
      >
        Back
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isRescheduleMode ? "Reschedule Appointment" : "Book an Appointment"}
        </h1>
        <p className="text-gray-500 mt-1">
          {isRescheduleMode
            ? "Select a new date and time for the appointment"
            : "Easily schedule a consultation with your patient"}
        </p>
      </div>

      <div className="max-w-6xl">
        <div className="flex items-center gap-4 mb-8">
          <Avatar
            size={64}
            src={patientData?.profileImage}
            style={{
              backgroundColor: patientData?.profileImage
                ? undefined
                : "#8B5CF6",
            }}
          >
            {!patientData?.profileImage &&
              (patientData?.name || patientData?.fullName)
                ?.charAt(0)
                ?.toUpperCase()}
          </Avatar>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {patientData?.name || patientData?.fullName || "Patient"}
            </h2>
            <p className="text-sm text-gray-500">Weight Loss Consultation</p>
            {isRescheduleMode && appointment?.appointmentTimestamp && (
              <p className="text-xs text-purple-600 mt-1">
                Current:{" "}
                {dayjs
                  .unix(appointment.appointmentTimestamp)
                  .format("MMM DD, YYYY - hh:mm A")}
              </p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Date
          </label>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            format="MM/DD/YYYY"
            placeholder="mm/dd/yyyy"
            disabledDate={disabledDate}
            className="w-full max-w-md"
            size="large"
          />
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Select Time
              {selectedDate && allSlots.length > 0 && (
                <span className="ml-2 text-xs text-gray-500">
                  ({availableSlotsCount} available out of {allSlots.length}{" "}
                  slots)
                </span>
              )}
            </label>
            <Select
              value={timePeriod}
              onChange={(value) => {
                setTimePeriod(value);
                setSelectedTime(null);
              }}
              className="w-36"
              size="large"
              options={[
                { value: "Morning", label: "Morning" },
                { value: "Afternoon", label: "Afternoon" },
              ]}
            />
          </div>

          {!selectedDate ? (
            <div className="text-center py-12 text-gray-500">
              Please select a date first
            </div>
          ) : loadingSlots ? (
            <div className="text-center py-12">
              <Spin />
              <p className="text-gray-500 mt-3">Loading available slots...</p>
            </div>
          ) : allSlots.length > 0 ? (
            <div className="grid grid-cols-7 gap-3">
              {allSlots.map((slot, index) => (
                <Button
                  key={index}
                  type={
                    selectedTime?.timestamp === slot.timestamp
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleTimeSelect(slot)}
                  disabled={!slot.isAvailable}
                  className={
                    selectedTime?.timestamp === slot.timestamp
                      ? "bg-purple-600 border-purple-600 hover:bg-purple-700"
                      : slot.isAvailable
                      ? "hover:border-purple-600 hover:text-purple-600"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }
                  size="large"
                >
                  {formatSlotTime(slot.timestamp)}
                </Button>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No slots available for {timePeriod.toLowerCase()}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Try selecting a different time period or date
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!selectedDate || !selectedTime}
            className="bg-purple-600 hover:bg-purple-700 border-purple-600 px-8"
          >
            {isRescheduleMode ? "Confirm Reschedule" : "Confirm Booking"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentBooking;
